import { createSelector } from 'reselect';
import { isAuthenticatedSession, SessionStatus } from 'session/sessionState';
import { UserConfigurationStatus } from 'session/userConfigurationState';

const getUserData = (state: any) => state.userReducer;
const getSessionStatus = (state: any) => state.userReducer.sessionStatus as SessionStatus;
const getUserConfigurationStatus = (state: any) =>
    state.userReducer.userConfigurationStatus as UserConfigurationStatus;
const getNameChangeStatus = (state: any) => state.userReducer.isNameChanged;
const getPasswordChangeStatus = (state: any) => state.userReducer.isPasswordChanged;
const getPreferencesChangeStatus = (state: any) => state.userReducer.isPreferencesChanged;
const preferencesUpdateError = (state: any) => state.userReducer.preferencesUpdateError;
const getPasswordChangeRequiredStatus = (state: any) => state.userReducer.passwordChangeRequired;
const getDefaultPasswordChangedStatus = (state: any) => state.userReducer.defaultPasswordChanged;
const getDateOfLastRefreshData = (state: any) => state.userReducer.dateOfLastRefresh;
const getGamesByUserData = (state: any) => state.userReducer.games;
const getGameConfigurationsByUserData = (state: any) => state.userReducer.gameConfigurations;
const getAreGameConfigurationsLoadingData = (state: any) =>
    state.userReducer.areGameConfigurationsLoading;
const getAllCurrenciesData = (state: any) => state.userReducer.allCurrencies;
const getCurrenciesByBrandData = (state: any) => state.userReducer.currenciesByBrand;
const getTimezoneListData = (state: any) => state.userReducer.timezoneList;
const getCountriesData = (state: any) => state.userReducer.countries;

// User operations
export const userState = createSelector(getUserData, (state) => state);
export const sessionStatus = createSelector(getSessionStatus, (status) => status);
export const userConfigurationStatus = createSelector(
    getUserConfigurationStatus,
    (status) => status,
);

// Compatibility selectors keep existing consumers on the canonical session state while they are
// migrated. They must never become independently stored Redux fields again.
export const authStatus = createSelector(getSessionStatus, (status) => {
    if (status === SessionStatus.INITIALIZING) {
        return 'unknown';
    }

    return isAuthenticatedSession(status) ? 'authenticated' : 'unauthenticated';
});
export const isAuthorised = createSelector(getSessionStatus, isAuthenticatedSession);
export const isLoggingOut = createSelector(
    getSessionStatus,
    (status) => status === SessionStatus.LOGGING_OUT,
);
export const isSessionBootstrapping = createSelector(
    getSessionStatus,
    (status) => status === SessionStatus.INITIALIZING,
);
export const isNameChanged = createSelector(getNameChangeStatus, (state) => state);
export const isPasswordChanged = createSelector(getPasswordChangeStatus, (state) => state);
export const isPreferencesChanged = createSelector(getPreferencesChangeStatus, (state) => state);
export const getPreferencesUpdateError = createSelector(preferencesUpdateError, (state) => state);
export const passwordChangeRequired = createSelector(
    getPasswordChangeRequiredStatus,
    (state) => state,
);
export const defaultPasswordChanged = createSelector(
    getDefaultPasswordChangedStatus,
    (state) => state,
);
export const getDateOfLastRefresh = createSelector(getDateOfLastRefreshData, (state) => state);
export const getGamesByUser = createSelector(getGamesByUserData, (state) => state);
export const getGameConfigurationsByUser = createSelector(
    getGameConfigurationsByUserData,
    (state) => state,
);
export const getAreGameConfigurationsLoading = createSelector(
    getAreGameConfigurationsLoadingData,
    (state) => state,
);
export const getAllCurrencies = createSelector(getAllCurrenciesData, (state) => state);
export const getCurrenciesByBrand = createSelector(getCurrenciesByBrandData, (state) => state);
export const getTimezoneList = createSelector(getTimezoneListData, (state) => state);
export const getCountries = createSelector(getCountriesData, (state) => state);
